import {
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from 'react'

import { apiGetJobLocations } from '@/features/jobs/api/api-get-job-locations'
import { apiGetSiteWorksPartners } from '@/features/jobs/api/api-get-site-works-partners'
import { apiGetUsers } from '@/features/jobs/api/api-get-users'
import { useLocationSearch } from '@/features/jobs/hooks/use-location-search'
import { formatJobType } from '@/features/jobs/lib/format-job-type'
import type { JobType } from '@/features/jobs/types/job'
import type { JobsReportBaseFilters } from '@/features/reports/types/jobs-report'
import type { FilterOption } from '@/lib/api/types'
import {
  Button,
  ComboboxField,
  InputField,
  Offcanvas,
  SelectField,
  type SelectOption,
} from '@/shared/components'

type JobsReportFiltersPanelProps<TFilters extends JobsReportBaseFilters> = {
  open: boolean
  onOpenChange: (open: boolean) => void
  filters: TFilters
  onApply: (filters: TFilters) => void
  title: string
  idPrefix: string
  emptyFilters: () => TFilters
  renderPrimaryDimension: (
    draft: TFilters,
    setDraft: Dispatch<SetStateAction<TFilters>>
  ) => ReactNode
}

const jobTypeOptions: SelectOption[] = (
  ['new_installation', 'remediation'] as JobType[]
).map((jobType) => ({
  value: jobType,
  label: formatJobType(jobType),
}))

const toSelectOptions = (options: FilterOption[]): SelectOption[] =>
  options.map((option) => ({
    value: String(option.id),
    label: option.label,
  }))

export const JobsReportFiltersPanel = <TFilters extends JobsReportBaseFilters>({
  open,
  onOpenChange,
  filters,
  onApply,
  title,
  idPrefix,
  emptyFilters,
  renderPrimaryDimension,
}: JobsReportFiltersPanelProps<TFilters>) => {
  const [draft, setDraft] = useState<TFilters>(filters)
  const [raisedByUserOptions, setRaisedByUserOptions] = useState<FilterOption[]>(
    []
  )
  const [completedByUserOptions, setCompletedByUserOptions] = useState<
    FilterOption[]
  >([])
  const [partnerOptions, setPartnerOptions] = useState<FilterOption[]>([])
  const [isLoadingOptions, setIsLoadingOptions] = useState(false)
  const [optionsError, setOptionsError] = useState<string | null>(null)
  const {
    locationSelectOptions,
    selectedLocationId,
    locationInputValue,
    isSearchingLocations,
    searchError,
    selectLocation,
    changeLocationInput,
    openLocationPicker,
    hydrateFromQuery,
  } = useLocationSearch({ enabled: open, search: apiGetJobLocations })
  const [syncedOpen, setSyncedOpen] = useState<boolean | null>(null)

  if (open !== syncedOpen) {
    setSyncedOpen(open)
    hydrateFromQuery(filters.location)

    if (open) {
      setDraft(filters)
    }
  }

  useEffect(() => {
    if (!open) {
      return
    }

    const loadFilterOptions = async () => {
      setIsLoadingOptions(true)
      setOptionsError(null)

      try {
        const [raisedByUsers, completedByUsers, partners] = await Promise.all([
          apiGetUsers({ exclude_role: 'site_worker' }),
          apiGetUsers({ role: 'site_worker' }),
          apiGetSiteWorksPartners(),
        ])

        setRaisedByUserOptions(raisedByUsers)
        setCompletedByUserOptions(completedByUsers)
        setPartnerOptions(partners)
      } catch (loadError) {
        setOptionsError(
          loadError instanceof Error
            ? loadError.message
            : 'Failed to load filter options'
        )
      } finally {
        setIsLoadingOptions(false)
      }
    }

    void loadFilterOptions()
  }, [open])

  const raisedByUserSelectOptions = useMemo(
    () => toSelectOptions(raisedByUserOptions),
    [raisedByUserOptions]
  )

  const completedByUserSelectOptions = useMemo(
    () => toSelectOptions(completedByUserOptions),
    [completedByUserOptions]
  )

  const partnerSelectOptions = useMemo(
    () => toSelectOptions(partnerOptions),
    [partnerOptions]
  )

  const handleApply = () => {
    onApply(draft)
    onOpenChange(false)
  }

  const handleClear = () => {
    const cleared = emptyFilters()
    setDraft(cleared)
    onApply(cleared)
    onOpenChange(false)
  }

  return (
    <Offcanvas
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <div className="flex gap-3">
          <Button
            type="button"
            variant="secondary"
            className="flex-1"
            onClick={handleClear}
          >
            Clear
          </Button>

          <Button
            type="button"
            variant="action"
            className="flex-1"
            onClick={handleApply}
          >
            Filter
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6">
        <InputField
          id={`${idPrefix}-completed-at-from`}
          label="Outcome completed from"
          type="date"
          value={draft.completed_at_from}
          onChange={(completed_at_from) =>
            setDraft((current) => ({ ...current, completed_at_from }))
          }
        />

        <InputField
          id={`${idPrefix}-completed-at-to`}
          label="Outcome completed to"
          type="date"
          value={draft.completed_at_to}
          onChange={(completed_at_to) =>
            setDraft((current) => ({ ...current, completed_at_to }))
          }
        />

        <SelectField
          id={`${idPrefix}-site-works-partner`}
          label="SiteWorks partner"
          value={
            draft.assigned_partner_id
              ? String(draft.assigned_partner_id)
              : undefined
          }
          onValueChange={(value) =>
            setDraft((current) => ({
              ...current,
              assigned_partner_id: value ? Number(value) : undefined,
            }))
          }
          options={partnerSelectOptions}
          placeholder="All"
          allowEmpty
          disabled={isLoadingOptions}
        />

        <SelectField
          id={`${idPrefix}-completed-by`}
          label="Outcome completed by"
          value={
            draft.completed_by_user_id
              ? String(draft.completed_by_user_id)
              : undefined
          }
          onValueChange={(value) =>
            setDraft((current) => ({
              ...current,
              completed_by_user_id: value ? Number(value) : undefined,
            }))
          }
          options={completedByUserSelectOptions}
          placeholder="All"
          allowEmpty
          disabled={isLoadingOptions}
        />

        {renderPrimaryDimension(draft, setDraft)}

        <InputField
          id={`${idPrefix}-date-raised`}
          label="Date raised"
          type="date"
          value={draft.date_raised}
          onChange={(date_raised) =>
            setDraft((current) => ({ ...current, date_raised }))
          }
        />

        <SelectField
          id={`${idPrefix}-raised-by`}
          label="Raised by"
          value={
            draft.raised_by_user_id
              ? String(draft.raised_by_user_id)
              : undefined
          }
          onValueChange={(value) =>
            setDraft((current) => ({
              ...current,
              raised_by_user_id: value ? Number(value) : undefined,
            }))
          }
          options={raisedByUserSelectOptions}
          placeholder="All"
          allowEmpty
          disabled={isLoadingOptions}
        />

        <SelectField
          id={`${idPrefix}-job-type`}
          label="Job type"
          value={draft.job_type}
          onValueChange={(job_type) =>
            setDraft((current) => ({
              ...current,
              job_type: job_type as JobType | undefined,
            }))
          }
          options={jobTypeOptions}
          placeholder="All"
          allowEmpty
        />

        <ComboboxField
          id={`${idPrefix}-location`}
          label="Location"
          placeholder="Search by location"
          value={selectedLocationId}
          onValueChange={(value) => {
            const location = selectLocation(value)

            if (!location) {
              return
            }

            setDraft((current) => ({
              ...current,
              location: location.location_id,
            }))
          }}
          inputValue={locationInputValue}
          onInputValueChange={(location) => {
            changeLocationInput(location)
            setDraft((current) => ({ ...current, location }))
          }}
          onFocus={openLocationPicker}
          options={locationSelectOptions}
          loading={isSearchingLocations}
        />

        {optionsError || searchError ? (
          <p className="text-sm text-destructive" role="alert">
            {optionsError ?? searchError}
          </p>
        ) : null}
      </div>
    </Offcanvas>
  )
}
